import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as provider from '../../data/community/communityDataProvider';
import { useCommunityDataSource, DATA_SOURCE_MOCK } from '../../utils/communityDataSource';
import ThreadDetailModal from '../../components/ThreadDetailModal/ThreadDetailModal';

function StatusBadge({ status }) {
  const open = Number(status) === 0;
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded text-xs border ${
        open
          ? 'bg-amber-500/20 text-amber-200 border-amber-400/40'
          : 'bg-green-500/20 text-green-200 border-green-400/40'
      }`}
    >
      {open ? 'Open' : 'Reviewed'}
    </span>
  );
}

/** Create-report form. Uses the real POST /threads/{id}/reports endpoint in API mode. */
function ReportComposer({ dataSource }) {
  const [threadId, setThreadId] = useState('1');
  const [reporterUserId, setReporterUserId] = useState('1001');
  const [reportType, setReportType] = useState('1');
  const [detail, setDetail] = useState('');
  const queryClient = useQueryClient();

  const submitMut = useMutation({
    mutationFn: () =>
      provider.postReport(
        Number(threadId),
        { report_type: Number(reportType), detail: detail || undefined },
        Number(reporterUserId),
      ),
    onSuccess: () => {
      setDetail('');
      queryClient.invalidateQueries({ queryKey: ['communityReports'] });
    },
  });

  return (
    <div className="bg-dark-card border border-dark-border rounded-lg p-4">
      <h3 className="text-sm font-semibold text-dark-text mb-3">Submit a report</h3>
      <div className="grid gap-3 sm:grid-cols-4">
        <div>
          <label className="text-xs text-dark-text-muted">Thread ID</label>
          <input
            className="w-full mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm text-dark-text"
            value={threadId}
            onChange={(e) => setThreadId(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs text-dark-text-muted">Reporter user ID</label>
          <input
            className="w-full mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm text-dark-text"
            value={reporterUserId}
            onChange={(e) => setReporterUserId(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs text-dark-text-muted">Report type (1-999)</label>
          <input
            className="w-full mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm text-dark-text"
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs text-dark-text-muted">Detail (optional)</label>
          <input
            className="w-full mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm text-dark-text"
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
          />
        </div>
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button
          type="button"
          className="px-4 py-2 bg-primary text-black rounded font-medium text-sm disabled:opacity-50"
          onClick={() => submitMut.mutate()}
          disabled={submitMut.isPending}
        >
          {submitMut.isPending ? 'Submitting…' : 'POST report'}
        </button>
        <span className="text-xs text-dark-text-muted">
          {dataSource === DATA_SOURCE_MOCK ? 'mock store' : 'live: POST /api/v1/community/threads/{id}/reports'}
        </span>
        {submitMut.isSuccess && <span className="text-xs text-green-300">Submitted.</span>}
        {submitMut.error && (
          <span className="text-xs text-red-400">
            status={submitMut.error.status ?? ''} code={submitMut.error.code ?? ''} —{' '}
            {submitMut.error.message}
          </span>
        )}
      </div>
    </div>
  );
}

export default function CommunityReports() {
  const [statusFilter, setStatusFilter] = useState('0');
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const dataSource = useCommunityDataSource();
  const isMock = dataSource === DATA_SOURCE_MOCK;

  const reportsQuery = useQuery({
    queryKey: ['communityReports', dataSource, statusFilter],
    queryFn: () =>
      provider.getReports({
        status: statusFilter === '' ? undefined : statusFilter,
        limit: 100,
      }),
    enabled: isMock,
  });

  const rows = reportsQuery.data?.data || [];

  return (
    <div className="space-y-4">
      <ReportComposer dataSource={dataSource} />

      {!isMock && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-200 text-xs px-4 py-3">
          The community API does not expose a reports-list endpoint (only{' '}
          <code>POST /threads/&#123;id&#125;/reports</code>). Reporting works live above; the table
          below is available in mock mode only.
        </div>
      )}

      {isMock && (
        <>
          <select
            className="bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="0">Open</option>
            <option value="1">Reviewed</option>
          </select>

          <div className="overflow-x-auto border border-dark-border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-dark-surface text-dark-text-secondary">
                <tr>
                  <th className="text-left p-2">ID</th>
                  <th className="text-left p-2">Thread</th>
                  <th className="text-left p-2">Reporter</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Detail</th>
                  <th className="text-left p-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-dark-border">
                    <td className="p-2">{r.id}</td>
                    <td className="p-2">
                      <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={() => setSelectedThreadId(r.thread_id)}
                      >
                        #{r.thread_id}
                      </button>
                    </td>
                    <td className="p-2">{r.reporter_user_id}</td>
                    <td className="p-2">{r.report_type}</td>
                    <td className="p-2">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="p-2 max-w-xs truncate">{r.detail || '—'}</td>
                    <td className="p-2 text-xs text-dark-text-muted">{r.created_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {selectedThreadId && (
        <ThreadDetailModal
          threadId={selectedThreadId}
          onClose={() => setSelectedThreadId(null)}
        />
      )}
    </div>
  );
}
